import { useEffect, useState } from "react";
import { api, brl } from "../lib/api";
import { AdminGate, AdminShell } from "./AdminGate";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

const GRUPOS = [
  ["Valores base", ["base_frete", "base_mudanca", "km_rate", "carona_rate"]],
  ["Dificuldade", ["diff_facil_pct", "diff_moderado_pct", "diff_dificil_pct"]],
  ["Ajudantes", ["helper_frete_facil", "helper_frete_dificil", "helper_mudanca_facil", "helper_mudanca_dificil"]],
  ["Escadas e Pedágio", ["escada_adicional_lance", "toll_multiplier"]],
  ["Distância e limites", ["surcharge_km", "surcharge_pct", "distancia_min_km", "distancia_max_km", "frete_max_itens"]],
  ["Regras internas — itens pesados e escadas", ["piano_unit", "cofre_unit", "escada_limite1", "escada_limite2", "helper_escada_t1", "helper_escada_t2", "diff_max_escada_pct", "agenda_capacidade"]],
];

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#F5B301]";

function Precos({ user }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [sinal, setSinal] = useState(30);
  const [saving, setSaving] = useState(false);
  const [comercial, setComercial] = useState(null);
  const [savingCom, setSavingCom] = useState("");
  const [promocoes, setPromocoes] = useState([]);
  const [promoForm, setPromoForm] = useState(null);

  const loadPromocoes = () => api.get("/admin/promocoes").then((r) => setPromocoes(r.data.promocoes || [])).catch(() => {});

  const salvarTaxa = async (key, label) => {
    setSavingCom(key);
    try {
      const c = comercial[key];
      const body = key === "acumular_taxas" ? { acumular_taxas: !!comercial.acumular_taxas }
        : key === "entrega" ? { entrega: { ...c, valor_base: parseFloat(c.valor_base) || 0, valor_km: parseFloat(c.valor_km) || 0, valor_minimo: parseFloat(c.valor_minimo) || 0 } }
        : { [key]: { ...c, percentual: parseFloat(c.percentual) || 0 } };
      await api.put("/admin/comercial", body);
      toast.success(`${label} salvo! Vale para os próximos orçamentos.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao salvar"); }
    finally { setSavingCom(""); }
  };

  const salvarPromo = async () => {
    setSavingCom("promo");
    try {
      const body = { nome: promoForm.nome, descricao: promoForm.descricao || "", percentual: parseFloat(promoForm.percentual) || 0,
                     data_inicio: promoForm.data_inicio || "", data_fim: promoForm.data_fim || "" };
      if (promoForm.promo_id) await api.put(`/admin/promocoes/${promoForm.promo_id}`, body);
      else await api.post("/admin/promocoes", body);
      toast.success(promoForm.promo_id ? "Promoção atualizada!" : "Promoção criada! Ative-a quando quiser aplicar o desconto.");
      setPromoForm(null);
      loadPromocoes();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao salvar promoção"); }
    finally { setSavingCom(""); }
  };

  const promoAcao = async (p, acao) => {
    if (acao === "encerrar" && !window.confirm(`Encerrar a promoção "${p.nome}" definitivamente? Ela não poderá ser reativada.`)) return;
    try {
      await api.post(`/admin/promocoes/${p.promo_id}/${acao}`);
      toast.success({ ativar: "Promoção ATIVA — desconto aplicado aos próximos orçamentos", desativar: "Promoção desativada", encerrar: "Promoção encerrada" }[acao]);
      loadPromocoes();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };

  const load = () =>
    api.get("/admin/pricing").then((r) => {
      setData(r.data);
      setForm({ ...r.data.pricing });
      setSinal(r.data.sinal_pct);
    }).catch((e) => {
      toast.error(e.response?.data?.detail || "Sem permissão para acessar preços");
      setData({ pricing: {}, labels: {} });
    });
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/admin/comercial").then((r) => setComercial(r.data)).catch(() => {}); loadPromocoes(); }, []);

  const Toggle = ({ testid, checked, onChange, label }) => (
    <label className="flex items-center justify-between gap-3 py-1 cursor-pointer" data-testid={testid}>
      <span className="text-xs font-semibold text-slate-600">{label}</span>
      <button type="button" onClick={() => onChange(!checked)}
        className={`w-11 h-6 rounded-full transition-colors relative ${checked ? "bg-emerald-500" : "bg-slate-300"}`}>
        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
      </button>
    </label>
  );

  const salvar = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.keys(form).forEach((k) => (payload[k] = parseFloat(form[k]) || 0));
      await api.put("/admin/pricing", { pricing: payload, sinal_pct: parseFloat(sinal) || 30 });
      toast.success("Preços e regras atualizados! Valem para os próximos orçamentos.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <AdminShell user={user} title="Preços e Regras">
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#F5B301]" /></div>
      </AdminShell>
    );
  }

  const L = data.labels || {};

  return (
    <AdminShell user={user} title="Preços e Regras">
      <div className="px-4 mt-4 space-y-4" data-testid="admin-precos-page">
        <div className="bg-gold-light border border-[#F5B301]/50 rounded-2xl p-3">
          <p className="text-[11px] text-navy font-semibold">
            Toda alteração salva aqui passa a valer automaticamente nos próximos orçamentos. Nada fica fixo no código.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Sinal de reserva (%)</label>
          <input data-testid="pricing-sinal" type="number" step="1" min="1" max="100" className={`${inputCls} mt-1`}
            value={sinal} onChange={(e) => setSinal(e.target.value)} />
        </div>

        {GRUPOS.map(([titulo, keys]) => (
          <div key={titulo} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3">
            <p className="text-xs font-extrabold text-navy uppercase tracking-wide">{titulo}</p>
            {keys.filter((k) => k in L).map((k) => (
              <div key={k}>
                <label className="text-[11px] font-semibold text-slate-500">{L[k]}</label>
                <input data-testid={`pricing-${k}`} type="number" step="0.01" min="0" className={`${inputCls} mt-1`}
                  value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              </div>
            ))}
          </div>
        ))}

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="regras-resumo">
          <p className="text-xs font-extrabold text-navy uppercase tracking-wide mb-2">Regras de cálculo (resumo)</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            <strong>Frete:</strong> base {brl(parseFloat(form.base_frete) || 0)} + {brl(parseFloat(form.km_rate) || 0)}/km +
            dificuldade ({form.diff_facil_pct}%/{form.diff_moderado_pct}%/{form.diff_dificil_pct}%) + ajudante + pedágio + carona.
          </p>
          <p className="text-xs text-slate-600 leading-relaxed mt-1.5">
            <strong>Mudança:</strong> base {brl(parseFloat(form.base_mudanca) || 0)} + {brl(parseFloat(form.km_rate) || 0)}/km +
            dificuldade + ajudantes + pedágio.
          </p>
          <p className="text-xs text-slate-600 leading-relaxed mt-1.5">
            <strong>Distância:</strong> acima de {form.surcharge_km} km aplica +{form.surcharge_pct}% sobre o subtotal.
          </p>
        </div>

        {comercial && (
          <>
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3" data-testid="promocoes-card">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-extrabold text-navy uppercase tracking-wide">🎯 Promoções</p>
                {!promoForm && (
                  <button data-testid="promo-nova-btn" onClick={() => setPromoForm({ nome: "", descricao: "", percentual: "", data_inicio: "", data_fim: "" })}
                    className="text-[10px] font-extrabold bg-[#F5B301] text-navy px-3 py-1.5 rounded-lg active:scale-95 transition-transform">
                    + CRIAR PROMOÇÃO
                  </button>
                )}
              </div>
              <p className="text-[10px] text-slate-400">Promoção é um <strong>desconto</strong> concedido ao cliente. Totalmente independente de urgência, final de semana, feriado e entrega.</p>

              {promoForm && (
                <div className="bg-slate-50 rounded-xl p-3 space-y-2" data-testid="promo-form">
                  <input data-testid="promo-form-nome" className={inputCls} placeholder="Nome da promoção (ex.: Black Friday)" value={promoForm.nome}
                    onChange={(e) => setPromoForm({ ...promoForm, nome: e.target.value })} />
                  <input data-testid="promo-form-descricao" className={inputCls} placeholder="Descrição (opcional)" value={promoForm.descricao}
                    onChange={(e) => setPromoForm({ ...promoForm, descricao: e.target.value })} />
                  <div className="flex items-center gap-2">
                    <input data-testid="promo-form-percentual" className={inputCls} type="number" min="1" max="100" placeholder="Desconto" value={promoForm.percentual}
                      onChange={(e) => setPromoForm({ ...promoForm, percentual: e.target.value })} />
                    <span className="text-xs font-bold text-slate-400 shrink-0">%</span>
                  </div>
                  <div className="flex gap-2">
                    <input data-testid="promo-form-inicio" className={inputCls} type="date" value={promoForm.data_inicio}
                      onChange={(e) => setPromoForm({ ...promoForm, data_inicio: e.target.value })} />
                    <input data-testid="promo-form-fim" className={inputCls} type="date" value={promoForm.data_fim}
                      onChange={(e) => setPromoForm({ ...promoForm, data_fim: e.target.value })} />
                  </div>
                  <p className="text-[10px] text-slate-400">Datas opcionais — em branco, vale a partir do momento em que for ativada.</p>
                  <div className="flex gap-2">
                    <button data-testid="promo-form-salvar" onClick={salvarPromo} disabled={savingCom === "promo"}
                      className="flex-1 py-2.5 rounded-xl bg-navy text-[#F5B301] text-[11px] font-extrabold active:scale-95 transition-transform disabled:opacity-60">
                      {savingCom === "promo" ? "Salvando..." : promoForm.promo_id ? "SALVAR ALTERAÇÕES" : "CRIAR PROMOÇÃO"}
                    </button>
                    <button data-testid="promo-form-cancelar" onClick={() => setPromoForm(null)}
                      className="px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-500 text-[11px] font-bold active:scale-95 transition-transform">
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-2" data-testid="promo-lista">
                {promocoes.length === 0 && !promoForm && <p className="text-xs text-slate-400">Nenhuma promoção criada ainda.</p>}
                {promocoes.map((p) => (
                  <div key={p.promo_id} className="border border-slate-100 rounded-xl p-3" data-testid={`promo-item-${p.promo_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="text-xs font-bold text-navy">{p.nome}</p>
                        <p className="text-[10px] text-slate-400">
                          DESCONTO: {p.percentual}%{p.data_inicio ? ` · de ${p.data_inicio.split("-").reverse().join("/")}` : ""}{p.data_fim ? ` até ${p.data_fim.split("-").reverse().join("/")}` : ""}
                          {p.descricao ? ` · ${p.descricao}` : ""}
                        </p>
                      </div>
                      <span className={`text-[9px] font-extrabold px-2 py-1 rounded-full shrink-0 ${
                        p.status === "ativa" ? "bg-emerald-100 text-emerald-700" : p.status === "encerrada" ? "bg-slate-200 text-slate-500" : "bg-amber-100 text-amber-700"}`}
                        data-testid={`promo-status-${p.promo_id}`}>
                        {p.status === "ativa" ? "ATIVA" : p.status === "encerrada" ? "ENCERRADA" : "INATIVA"}
                      </span>
                    </div>
                    {p.status !== "encerrada" && (
                      <div className="flex gap-1.5 mt-2">
                        {p.status !== "ativa" && (
                          <button data-testid={`promo-ativar-${p.promo_id}`} onClick={() => promoAcao(p, "ativar")}
                            className="flex-1 py-1.5 rounded-lg bg-emerald-600 text-white text-[10px] font-bold active:scale-95 transition-transform">ATIVAR</button>
                        )}
                        {p.status === "ativa" && (
                          <button data-testid={`promo-desativar-${p.promo_id}`} onClick={() => promoAcao(p, "desativar")}
                            className="flex-1 py-1.5 rounded-lg bg-amber-500 text-white text-[10px] font-bold active:scale-95 transition-transform">DESATIVAR</button>
                        )}
                        <button data-testid={`promo-editar-${p.promo_id}`} onClick={() => setPromoForm({ promo_id: p.promo_id, nome: p.nome, descricao: p.descricao || "", percentual: p.percentual, data_inicio: p.data_inicio || "", data_fim: p.data_fim || "" })}
                          className="flex-1 py-1.5 rounded-lg bg-navy text-[#F5B301] text-[10px] font-bold active:scale-95 transition-transform">EDITAR</button>
                        <button data-testid={`promo-encerrar-${p.promo_id}`} onClick={() => promoAcao(p, "encerrar")}
                          className="flex-1 py-1.5 rounded-lg bg-red-600 text-white text-[10px] font-bold active:scale-95 transition-transform">ENCERRAR</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {[["urgencia", "🚨 Taxa de urgência", "urg", "O cliente marca 'preciso com urgência' no orçamento e paga o adicional. Independente de promoção."],
              ["final_de_semana", "📅 Acréscimo de final de semana", "fds", "Aplicado automaticamente em sábados e domingos; o cliente vê apenas o valor final. Independente de promoção."],
              ["feriado", "🎉 Acréscimo de feriado", "fer", "Aplicado automaticamente em feriados nacionais; o cliente vê apenas o valor final. Independente de promoção."]].map(([k, label, tid, hint]) => (
              <div key={k} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-2" data-testid={`${k}-card`}>
                <Toggle testid={`${tid}-ativo`} checked={!!comercial[k]?.ativo} label={label}
                  onChange={(v) => setComercial({ ...comercial, [k]: { ...comercial[k], ativo: v } })} />
                <div className="flex items-center gap-2">
                  <input data-testid={`${tid}-percentual`} className={inputCls} type="number" min="0" step="0.5" placeholder="Percentual"
                    value={comercial[k]?.percentual ?? ""}
                    onChange={(e) => setComercial({ ...comercial, [k]: { ...comercial[k], percentual: e.target.value } })} />
                  <span className="text-xs font-bold text-slate-400 shrink-0">%</span>
                </div>
                <p className="text-[10px] text-slate-400">{hint}</p>
                <button data-testid={`${tid}-save`} onClick={() => salvarTaxa(k, label)} disabled={savingCom === k}
                  className="w-full py-2.5 rounded-xl bg-navy text-[#F5B301] text-[10px] font-extrabold active:scale-95 transition-transform disabled:opacity-60">
                  {savingCom === k ? "Salvando..." : "SALVAR"}
                </button>
              </div>
            ))}

            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-2" data-testid="acumulo-card">
              <Toggle testid="acumulo-toggle" checked={!!comercial.acumular_taxas} label="Permitir acúmulo de taxas (urgência + fim de semana + feriado)"
                onChange={(v) => setComercial({ ...comercial, acumular_taxas: v })} />
              <p className="text-[10px] text-slate-400">{comercial.acumular_taxas ? "As taxas ativas somam entre si." : "Sem acúmulo: aplica-se apenas a maior taxa."}</p>
              <button data-testid="acumulo-save" onClick={() => salvarTaxa("acumular_taxas", "Acúmulo de taxas")} disabled={savingCom === "acumular_taxas"}
                className="w-full py-2.5 rounded-xl bg-navy text-[#F5B301] text-[10px] font-extrabold active:scale-95 transition-transform disabled:opacity-60">
                {savingCom === "acumular_taxas" ? "Salvando..." : "SALVAR"}
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-2" data-testid="entrega-card">
              <Toggle testid="entrega-ativo" checked={!!comercial.entrega?.ativo} label="🏍️ Liberar categoria ENTREGA para clientes"
                onChange={(v) => setComercial({ ...comercial, entrega: { ...comercial.entrega, ativo: v } })} />
              {!comercial.entrega?.ativo && (
                <p className="text-[10px] text-amber-600 bg-amber-50 rounded-lg p-2">Entrega DESABILITADA — o cliente vê a categoria, mas não consegue contratar até você configurar, salvar e ativar.</p>
              )}
              <div className="grid grid-cols-3 gap-2">
                <input data-testid="entrega-valor-base" className={inputCls} type="number" min="0" placeholder="Base R$" value={comercial.entrega?.valor_base ?? ""}
                  onChange={(e) => setComercial({ ...comercial, entrega: { ...comercial.entrega, valor_base: e.target.value } })} />
                <input data-testid="entrega-valor-km" className={inputCls} type="number" min="0" step="0.1" placeholder="R$/km" value={comercial.entrega?.valor_km ?? ""}
                  onChange={(e) => setComercial({ ...comercial, entrega: { ...comercial.entrega, valor_km: e.target.value } })} />
                <input data-testid="entrega-valor-minimo" className={inputCls} type="number" min="0" placeholder="Mínimo R$" value={comercial.entrega?.valor_minimo ?? ""}
                  onChange={(e) => setComercial({ ...comercial, entrega: { ...comercial.entrega, valor_minimo: e.target.value } })} />
              </div>
              <button data-testid="entrega-save" onClick={() => salvarTaxa("entrega", "Configuração de entrega")} disabled={savingCom === "entrega"}
                className="w-full py-2.5 rounded-xl bg-navy text-[#F5B301] text-[10px] font-extrabold active:scale-95 transition-transform disabled:opacity-60">
                {savingCom === "entrega" ? "Salvando..." : "SALVAR ENTREGA"}
              </button>
            </div>
          </>
        )}

        <button data-testid="pricing-save" onClick={salvar} disabled={saving}
          className="w-full py-4 rounded-2xl bg-[#F5B301] text-navy font-heading font-extrabold text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-4 h-4" /> SALVAR PREÇOS E REGRAS</>}
        </button>
        <p className="text-center text-[10px] text-slate-400 pb-2">Alterações registradas no Histórico/Logs.</p>
      </div>
    </AdminShell>
  );
}

export default function AdminPricing() {
  return <AdminGate>{(user) => <Precos user={user} />}</AdminGate>;
}
